"use client";

import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faShare,
  faBasketball,
  faCopy,
  faCheck,
  faDownload,
  faLink,
  faQrcode,
  faRocket,
  faExclamationTriangle,
  faArrowRight,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import useStore from "@/store/useStore";
import { supabase } from "@/utils/supabase/client";
import QensaQRCode from "@/components/ui/QRCode";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { hasConnectedAccount } from "@/utils/connectedAccounts";
import { isLightBackground, calculateAverageColor } from "@/utils/themeUtils";

const ShareModal = ({ isOpen, onClose, spaceData, onPublish }) => {
  const [selectedBackground, setSelectedBackground] = useState("white");
  const [isSubmittingToDribbble, setIsSubmittingToDribbble] = useState(false);
  const [dribbbleTitle, setDribbbleTitle] = useState("");
  const [dribbbleDescription, setDribbbleDescription] = useState("");
  const [dribbbleTags, setDribbbleTags] = useState("");
  const [copiedStates, setCopiedStates] = useState({});
  const [showDribbbleForm, setShowDribbbleForm] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [dribbbleAuthStatus, setDribbbleAuthStatus] = useState("checking"); // checking, connected, not_connected
  const [isConnectingDribbble, setIsConnectingDribbble] = useState(false);
  const [compositeImageUrls, setCompositeImageUrls] = useState({});
  const [isGeneratingComposite, setIsGeneratingComposite] = useState(false);
  const [averageBackgroundColor, setAverageBackgroundColor] = useState(null);
  const showToast = useStore((state) => state.showToast);
  const session = useStore((state) => state.session);

  // Determine if we should use dark glass based on the space's background (like space header buttons)
  const useDarkGlass = isLightBackground(spaceData, averageBackgroundColor);

  // Check if space is published
  const isSpacePublished = () => {
    const status = spaceData?.space_data?.status;
    return status === "published";
  };

  // Check if user has Dribbble account connected
  const checkDribbbleConnection = async () => {
    if (!session?.user?.id) {
      setDribbbleAuthStatus("not_connected");
      return;
    }

    try {
      // Check if token exists in user settings
      const userSettings = session.user.user_settings;

      // If user settings are available in session, check there first
      if (userSettings && hasConnectedAccount(userSettings, "dribbble")) {
        setDribbbleAuthStatus("connected");
        return;
      }

      // Otherwise fetch from database
      const { data, error } = await supabase
        .from("user_settings")
        .select("connected_accounts")
        .eq("id", session.user.id)
        .single();

      if (error) {
        console.error("Error checking Dribbble connection:", error);
        setDribbbleAuthStatus("not_connected");
        return;
      }

      setDribbbleAuthStatus(
        data?.connected_accounts?.dribbble ? "connected" : "not_connected"
      );
    } catch (error) {
      console.error("Error checking Dribbble connection:", error);
      setDribbbleAuthStatus("not_connected");
    }
  };

  // Handle Dribbble OAuth connection
  const handleDribbbleConnect = async () => {
    try {
      setIsConnectingDribbble(true);

      // Create a state parameter that includes current space info for redirect
      const currentPath = window.location.pathname;
      const state = JSON.stringify({
        type: "share_modal",
        timestamp: Date.now(),
        returnTo: currentPath,
        random: Math.random().toString(36).substring(7),
      });

      // Use the existing Dribbble API route to initiate OAuth
      const authUrl = `/api/dribbble/shots?state=${encodeURIComponent(state)}`;

      // Open in same window to handle the OAuth flow
      window.location.href = authUrl;
    } catch (error) {
      console.error("Error connecting to Dribbble:", error);
      showToast({
        title: "Connection Failed",
        description: "Failed to connect to Dribbble. Please try again.",
        variant: "destructive",
      });
      setIsConnectingDribbble(false);
    }
  };

  // Initialize Dribbble form with space data when form is shown
  useEffect(() => {
    if (showDribbbleForm && spaceData) {
      setDribbbleTitle(spaceData.space_data?.title || "My Qensa Space");
      setDribbbleDescription(
        spaceData.space_data?.description || "Check out my Qensa space!"
      );
      // Convert space tags to Dribbble format if available
      const spaceTags = spaceData.tags?.map((tag) => tag.name).join(", ") || "";
      setDribbbleTags(spaceTags ? `qensa, ${spaceTags}` : "qensa, portfolio");
    }
  }, [showDribbbleForm, spaceData]);

  // Calculate average background color for image backgrounds
  useEffect(() => {
    if (typeof window === "undefined") return;

    const background = spaceData?.space_data?.theme?.background;
    if (background?.type === "image" && background.media) {
      // Safely get media URL for color calculation
      const mediaUrl =
        typeof background.media === "string"
          ? background.media
          : background.media?.url || null;

      if (mediaUrl) {
        calculateAverageColor(mediaUrl, setAverageBackgroundColor);
      }
    } else {
      setAverageBackgroundColor(null);
      // If no background image and "original" is selected, reset to white
      if (selectedBackground === "original") {
        setSelectedBackground("white");
      }
    }
  }, [spaceData?.space_data?.theme?.background, selectedBackground]);

  // Check Dribbble connection when modal opens
  useEffect(() => {
    if (isOpen && session?.user?.id) {
      checkDribbbleConnection();
    }
  }, [isOpen, session?.user?.id]);

  // Handle successful Dribbble connection from OAuth redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("dribbble_connected") === "true") {
      // Remove the parameter from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);

      // Show success message
      showToast({
        title: "Dribbble Connected!",
        description: "Your Dribbble account has been successfully connected.",
        variant: "default",
      });

      // Update auth status and show form
      setDribbbleAuthStatus("connected");
      setIsConnectingDribbble(false);

      // Auto-open the share modal if not already open
      if (!isOpen) {
        // Note: This would need to be handled by parent component
        console.log("Dribbble connected, but modal is not open");
      }
    }
  }, [isOpen, showToast]);

  // Pre-generate composite images when background changes
  useEffect(() => {
    const baseUrl = spaceData?.space_data?.preview_image_url;
    if (isOpen && baseUrl && !isGeneratingComposite) {
      let backgroundColor = "#ffffff";
      let backgroundImageUrl = null;

      if (selectedBackground === "white") {
        backgroundColor = "#ffffff";
      } else if (selectedBackground === "black") {
        backgroundColor = "#000000";
      } else if (selectedBackground === "original") {
        // Get the actual background image URL from space data
        const background = spaceData?.space_data?.theme?.background;
        if (background?.type === "image" && background.media) {
          backgroundImageUrl =
            typeof background.media === "string"
              ? background.media
              : background.media?.url || null;
        }
      }

      // Generate preview size (1600x1200)
      const previewCacheKey = `${selectedBackground}-1600-1200`;
      if (!compositeImageUrls[previewCacheKey]) {
        generateCompositeImage(
          baseUrl,
          backgroundColor,
          1600,
          1200,
          previewCacheKey,
          backgroundImageUrl
        );
      }

      // Pre-generate Dribbble size (800x600) for faster submission
      const dribbbleCacheKey = `${selectedBackground}-800-600`;
      if (!compositeImageUrls[dribbbleCacheKey]) {
        generateCompositeImage(
          baseUrl,
          backgroundColor,
          800,
          600,
          dribbbleCacheKey,
          backgroundImageUrl
        );
      }
    }
  }, [
    isOpen,
    selectedBackground,
    spaceData?.space_data?.preview_image_url,
    spaceData?.space_data?.theme?.background?.media,
    spaceData?.space_data?.theme?.background?.type,
  ]);

  // Reset form state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowDribbbleForm(false);
      setShowQRCode(false);
      setDribbbleTitle("");
      setDribbbleDescription("");
      setDribbbleTags("");
      setDribbbleAuthStatus("checking");
      setIsConnectingDribbble(false);

      // Clean up composite image URLs to free memory
      Object.values(compositeImageUrls).forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
      setCompositeImageUrls({});

      // Clear failed requests and counts when modal closes
      setFailedRequests(new Set());
      setRequestCounts(new Map());
    }
  }, [isOpen]);

  // Get preview image URL with background
  const getPreviewImageUrl = (background, width = 1600, height = 1200) => {
    const baseUrl = spaceData?.space_data?.preview_image_url;
    if (!baseUrl) return null;

    // Check if we have a cached composite image for any background type
    const cacheKey = `${background}-${width}-${height}`;
    if (compositeImageUrls[cacheKey]) {
      return compositeImageUrls[cacheKey];
    }

    // Generate composite image if not cached
    let backgroundColor = "#ffffff";
    let backgroundImageUrl = null;

    if (background === "white") {
      backgroundColor = "#ffffff";
    } else if (background === "black") {
      backgroundColor = "#000000";
    } else if (background === "original") {
      // Get the actual background image URL from space data
      const spaceBackground = spaceData?.space_data?.theme?.background;
      if (spaceBackground?.type === "image" && spaceBackground.media) {
        backgroundImageUrl =
          typeof spaceBackground.media === "string"
            ? spaceBackground.media
            : spaceBackground.media?.url || null;
      }
    }

    generateCompositeImage(
      baseUrl,
      backgroundColor,
      width,
      height,
      cacheKey,
      backgroundImageUrl
    );

    // Return original image while composite is being generated
    return baseUrl;
  };

  // Track failed requests to prevent infinite retries
  const [failedRequests, setFailedRequests] = useState(new Set());
  const [requestCounts, setRequestCounts] = useState(new Map());

  // Generate composite image and cache it
  const generateCompositeImage = async (
    imageUrl,
    backgroundColor,
    width,
    height,
    cacheKey,
    backgroundImageUrl = null
  ) => {
    // Prevent multiple simultaneous requests
    if (isGeneratingComposite) return;

    // Check if this request has already failed
    if (failedRequests.has(cacheKey)) {
      console.log(`Skipping failed request for ${cacheKey}`);
      return;
    }

    // Check request count limit (max 3 attempts per cache key)
    const currentCount = requestCounts.get(cacheKey) || 0;
    if (currentCount >= 3) {
      console.log(`Max retry limit reached for ${cacheKey}`);
      setFailedRequests((prev) => new Set([...prev, cacheKey]));
      return;
    }

    try {
      setIsGeneratingComposite(true);
      setRequestCounts(
        (prev) => new Map([...prev, [cacheKey, currentCount + 1]])
      );

      const response = await fetch("/api/composite-image", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          imageUrl,
          backgroundColor,
          width,
          height,
          backgroundImageUrl,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        const error = new Error(
          `Failed to composite image: ${response.status} - ${errorText}`
        );

        // Check if this is a server configuration error that shouldn't be retried
        if (
          response.status === 500 &&
          (errorText.includes("Logo file not found") ||
            errorText.includes("server configuration issue") ||
            errorText.includes("Failed to load logo file"))
        ) {
          error.isPermanent = true;
        }

        throw error;
      }

      const blob = await response.blob();
      const dataUrl = URL.createObjectURL(blob);

      // Cache the composite image
      setCompositeImageUrls((prev) => ({
        ...prev,
        [cacheKey]: dataUrl,
      }));

      // Reset request count on success
      setRequestCounts((prev) => {
        const newMap = new Map(prev);
        newMap.delete(cacheKey);
        return newMap;
      });
    } catch (error) {
      console.error(
        `Error compositing image (attempt ${currentCount + 1}):`,
        error
      );

      // Check if this is a permanent error that shouldn't be retried
      if (error.isPermanent) {
        console.log(
          `Permanent error detected for ${cacheKey}, marking as failed`
        );
        setFailedRequests((prev) => new Set([...prev, cacheKey]));
        showToast({
          title: "Server Configuration Error",
          description:
            "Image processing failed due to server configuration. Please try again later.",
          variant: "destructive",
        });
      } else if (currentCount >= 2) {
        // Mark as failed after 3 attempts for non-permanent errors
        setFailedRequests((prev) => new Set([...prev, cacheKey]));
        showToast({
          title: "Image Processing Failed",
          description:
            "Unable to generate composite image after multiple attempts",
          variant: "destructive",
        });
      }
    } finally {
      setIsGeneratingComposite(false);
    }
  };

  // Get composite image blob for download/upload
  const getCompositeImageBlob = async (
    imageUrl,
    backgroundColor,
    width,
    height,
    backgroundImageUrl = null
  ) => {
    try {
      const response = await fetch("/api/composite-image", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          imageUrl,
          backgroundColor,
          width,
          height,
          backgroundImageUrl,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        const error = new Error(
          `Failed to composite image: ${response.status} - ${errorText}`
        );

        // Check if this is a server configuration error
        if (
          response.status === 500 &&
          (errorText.includes("Logo file not found") ||
            errorText.includes("server configuration issue") ||
            errorText.includes("Failed to load logo file"))
        ) {
          error.isPermanent = true;
        }

        throw error;
      }

      return await response.blob();
    } catch (error) {
      console.error("Error compositing image:", error);
      throw error;
    }
  };

  const backgroundOptions = [
    { id: "white", name: "White", color: "#ffffff" },
    { id: "black", name: "Black", color: "#000000" },
    // Only show original option if space has a background image
    ...(spaceData?.space_data?.theme?.background?.type === "image" &&
    spaceData?.space_data?.theme?.background?.media
      ? [{ id: "original", name: "Original", color: "transparent" }]
      : []),
  ];

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates({ ...copiedStates, [type]: true });
      showToast({
        title: "Copied!",
        description: `${type} copied to clipboard`,
        variant: "default",
      });
      setTimeout(() => {
        setCopiedStates({ ...copiedStates, [type]: false });
      }, 2000);
    } catch (error) {
      showToast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  const shareOptions = [
    {
      id: "link",
      name: "Copy Link",
      icon: faLink,
      action: () => {
        const url = `${window.location.origin}/${spaceData?.space_data?.slug}`;
        copyToClipboard(url, "Link");
      },
    },
    {
      id: "qr",
      name: "QR Code",
      icon: faQrcode,
      action: () => {
        setShowQRCode(true);
      },
    },
    {
      id: "download",
      name: "Download Image",
      icon: faDownload,
      action: async () => {
        const baseUrl = spaceData?.space_data?.preview_image_url;
        if (!baseUrl) return;

        try {
          let blob;
          let backgroundColor = "#ffffff";
          let backgroundImageUrl = null;

          if (selectedBackground === "white") {
            backgroundColor = "#ffffff";
          } else if (selectedBackground === "black") {
            backgroundColor = "#000000";
          } else if (selectedBackground === "original") {
            // Get the actual background image URL from space data
            const spaceBackground = spaceData?.space_data?.theme?.background;
            if (spaceBackground?.type === "image" && spaceBackground.media) {
              backgroundImageUrl =
                typeof spaceBackground.media === "string"
                  ? spaceBackground.media
                  : spaceBackground.media?.url || null;
            }
          }

          // Always use composite for consistent processing and logo placement
          blob = await getCompositeImageBlob(
            baseUrl,
            backgroundColor,
            1600,
            1200,
            backgroundImageUrl
          );

          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${spaceData?.space_data?.slug}-qensa.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch (error) {
          showToast({
            title: "Download Failed",
            description: "Failed to download image",
            variant: "destructive",
          });
        }
      },
    },
  ];

  const handleDribbbleSubmit = async () => {
    if (!dribbbleTitle.trim()) {
      showToast({
        title: "Error",
        description: "Please enter a title for your Dribbble shot",
        variant: "destructive",
      });
      return;
    }

    setIsSubmittingToDribbble(true);

    try {
      const baseUrl = spaceData?.space_data?.preview_image_url;
      if (!baseUrl) {
        throw new Error("No preview image available");
      }

      // Generate the image at exactly 800x600 for Dribbble
      let backgroundColor = "#ffffff";
      let backgroundImageUrl = null;

      if (selectedBackground === "white") {
        backgroundColor = "#ffffff";
      } else if (selectedBackground === "black") {
        backgroundColor = "#000000";
      } else if (selectedBackground === "original") {
        // Get the actual background image URL from space data
        const spaceBackground = spaceData?.space_data?.theme?.background;
        if (spaceBackground?.type === "image" && spaceBackground.media) {
          backgroundImageUrl =
            typeof spaceBackground.media === "string"
              ? spaceBackground.media
              : spaceBackground.media?.url || null;
        }
      }

      const imageBlob = await getCompositeImageBlob(
        baseUrl,
        backgroundColor,
        800,
        600,
        backgroundImageUrl
      );

      // Convert blob to base64 for Dribbble API
      const reader = new FileReader();
      const imageBase64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(imageBlob);
      });

      // Create the Dribbble shot data
      const dribbbleData = {
        title: dribbbleTitle.trim(),
        description: dribbbleDescription.trim(),
        tags: dribbbleTags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag),
        image: imageBase64,
        low_profile: false, // Set to true if you want it to be a low-profile shot
      };

      // Get user session for authentication
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error("Authentication required");
      }

      // Call the Dribbble API through your backend
      const response = await fetch("/api/dribbble/shots", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(dribbbleData),
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Handle specific error types
        if (response.status === 403) {
          // Display detailed 403 error information
          const errorMessage = errorData.error || "Permission denied";
          const suggestions = errorData.suggestions || [];
          const causes = errorData.possibleCauses || [];

          let detailedMessage = errorMessage;
          if (causes.length > 0) {
            detailedMessage += "\n\nPossible causes:\n• " + causes.join("\n• ");
          }
          if (suggestions.length > 0) {
            detailedMessage +=
              "\n\nSuggestions:\n• " + suggestions.join("\n• ");
          }

          throw new Error(detailedMessage);
        } else if (response.status === 422) {
          throw new Error(
            "Invalid shot data. Please ensure your image meets Dribbble's requirements (800x600 pixels, under 8MB, proper content)."
          );
        } else if (errorData.needsAuth) {
          throw new Error(
            "Please reconnect your Dribbble account in settings and try again."
          );
        }

        throw new Error(errorData.error || "Failed to submit to Dribbble");
      }

      const result = await response.json();

      showToast({
        title: "Success!",
        description: "Your Qensa has been submitted to Dribbble",
        variant: "default",
      });

      // Optionally open the Dribbble shot in a new tab
      if (result.html_url) {
        window.open(result.html_url, "_blank");
      }
    } catch (error) {
      console.error("Error submitting to Dribbble:", error);
      showToast({
        title: "Dribbble Upload Failed",
        description: error.message || "Failed to submit to Dribbble",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingToDribbble(false);
    }
  };

  // Publish Prompt Component
  const PublishPrompt = () => (
    <div className="flex flex-col items-center justify-center h-full text-center p-4 sm:p-8">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          delay: 0.2,
          type: "spring",
          stiffness: 300,
        }}
        className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center mb-4 sm:mb-6"
      >
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="text-white text-lg sm:text-2xl"
        />
      </motion.div>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.3 }}
        className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 ${useDarkGlass ? "text-white" : "text-gray-900"}`}
      >
        Publish to Share
      </motion.h3>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.3 }}
        className={`mb-6 sm:mb-8 max-w-md leading-relaxed text-sm sm:text-base ${useDarkGlass ? "text-gray-300" : "text-gray-600"}`}
      >
        Your space needs to be published before it can be shared. Publishing
        makes your space accessible to others and generates a shareable preview.
      </motion.p>

      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.3 }}
        onClick={() => {
          if (onPublish) {
            onPublish();
          }
          onClose();
        }}
        className="bg-gradient-to-r from-primary to-accent-blue hover:from-primary-dark hover:to-accent-blue-dark text-white px-6 sm:px-8 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 shadow-lg text-sm sm:text-base"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <FontAwesomeIcon icon={faRocket} className="mr-2" />
        Publish Space
      </motion.button>
    </div>
  );

  if (!isOpen) return null;

  const modalContent = (
    <TooltipProvider>
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{
              scale: 0.7,
              opacity: 0,
              y: 60,
              rotateX: 15,
            }}
            animate={{
              scale: 1,
              opacity: 1,
              y: 0,
              rotateX: 0,
            }}
            exit={{
              scale: 0.8,
              opacity: 0,
              y: -40,
              rotateX: -10,
            }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 300,
              mass: 0.8,
              duration: 0.4,
            }}
            className="relative w-full max-w-6xl h-full max-h-[90vh] sm:h-[600px] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{
              transformStyle: "preserve-3d",
              perspective: "1000px",
            }}
          >
            {/* Main glass container */}
            <div
              className="absolute inset-0 rounded-lg sm:rounded-[2rem]"
              style={{
                background: useDarkGlass
                  ? "rgba(0, 0, 0, 0.2)"
                  : "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(40px)",
                WebkitBackdropFilter: "blur(40px)",
                border: useDarkGlass
                  ? "1px solid rgba(0, 0, 0, 0.3)"
                  : "1px solid rgba(255, 255, 255, 0.3)",
                boxShadow: useDarkGlass
                  ? "0 8px 32px 0 rgba(0, 0, 0, 0.3)"
                  : "0 8px 32px 0 rgba(0, 0, 0, 0.1)",
              }}
            />

            {/* Close button */}
            <div className="absolute -top-2 -right-2 z-50">
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ delay: 0.1, duration: 0.2 }}
                    onClick={onClose}
                    className={`w-10 h-10 sm:w-8 sm:h-8 rounded-full transition-colors flex items-center justify-center ${
                      useDarkGlass
                        ? "bg-black/30 hover:bg-black/40"
                        : "bg-white/30 hover:bg-white/40"
                    }`}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FontAwesomeIcon
                      icon={faTimes}
                      className={`${useDarkGlass ? "text-white" : "text-gray-700"} text-lg sm:text-sm`}
                    />
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-gray-900">
                  <p>Close</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Modal content */}
            <div className="relative flex flex-col lg:flex-row h-full rounded-lg sm:rounded-[2rem] overflow-y-auto max-h-[calc(90vh-2rem)] sm:overflow-hidden sm:max-h-none p-3 sm:p-6 gap-3 sm:gap-6">
              {!isSpacePublished() ? (
                // Show publish prompt when space is not published
                <div className="w-full relative">
                  <div
                    className="absolute inset-0 rounded-lg sm:rounded-[2rem]"
                    style={{
                      background: useDarkGlass
                        ? "rgba(0, 0, 0, 0.6)"
                        : "rgba(255, 255, 255, 0.6)",
                      backdropFilter: "blur(20px)",
                      WebkitBackdropFilter: "blur(20px)",
                      border: useDarkGlass
                        ? "1px solid rgba(0, 0, 0, 0.8)"
                        : "1px solid rgba(255, 255, 255, 0.8)",
                    }}
                  />
                  <div className="relative h-full">
                    <PublishPrompt />
                  </div>
                </div>
              ) : (
                <>
                  {/* Left side - Preview */}
                  <div className="w-full lg:w-1/2 relative">
                    <div
                      className="absolute inset-0 rounded-lg sm:rounded-[2rem]"
                      style={{
                        background: useDarkGlass
                          ? "rgba(0, 0, 0, 0.6)"
                          : "rgba(255, 255, 255, 0.6)",
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",
                        border: useDarkGlass
                          ? "1px solid rgba(0, 0, 0, 0.8)"
                          : "1px solid rgba(255, 255, 255, 0.8)",
                      }}
                    />

                    <div className="relative flex flex-col h-full p-3 sm:p-6">
                      {/* Header */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.3 }}
                        className="flex items-center space-x-3 mb-4 sm:mb-6"
                      >
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{
                            delay: 0.3,
                            type: "spring",
                            stiffness: 300,
                          }}
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-primary to-accent-blue flex items-center justify-center"
                        >
                          <FontAwesomeIcon
                            icon={faShare}
                            className="text-white text-sm sm:text-lg"
                          />
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.35, duration: 0.3 }}
                        >
                          <h2
                            className={`text-lg sm:text-xl font-semibold ${useDarkGlass ? "text-white" : "text-gray-900"}`}
                          >
                            Share your Qensa
                          </h2>
                          <p
                            className={`text-xs sm:text-sm ${useDarkGlass ? "text-gray-300" : "text-gray-600"}`}
                          >
                            Export and share your space
                          </p>
                        </motion.div>
                      </motion.div>

                      {/* Preview Image */}
                      <div className="flex-1 flex flex-col items-center justify-center">
                        <div className="w-full max-w-sm">
                          {/* Background selector */}
                          <div className="mb-3 sm:mb-4">
                            <p
                              className={`text-xs sm:text-sm font-medium mb-2 ${useDarkGlass ? "text-white" : "text-gray-900"}`}
                            >
                              Choose background color
                            </p>
                            <div className="flex gap-2">
                              {backgroundOptions.map((option) => (
                                <button
                                  key={option.id}
                                  onClick={() =>
                                    setSelectedBackground(option.id)
                                  }
                                  className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full border-2 transition-all ${
                                    selectedBackground === option.id
                                      ? "border-primary scale-110"
                                      : "border-gray-300 hover:border-gray-400"
                                  }`}
                                  style={{
                                    backgroundColor: option.color,
                                    background:
                                      option.id === "original"
                                        ? (() => {
                                            // Try to use actual background image if available
                                            const background =
                                              spaceData?.space_data?.theme
                                                ?.background;
                                            if (
                                              background?.type === "image" &&
                                              background.media
                                            ) {
                                              const mediaUrl =
                                                typeof background.media ===
                                                "string"
                                                  ? background.media
                                                  : background.media?.url ||
                                                    null;
                                              if (mediaUrl) {
                                                return `url(${mediaUrl})`;
                                              }
                                            }
                                            // Fallback to gradient pattern
                                            return "conic-gradient(from 0deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4, #feca57, #ff9ff3, #f368e0, #ff6b6b)";
                                          })()
                                        : option.color,
                                    backgroundSize:
                                      option.id === "original"
                                        ? "cover"
                                        : "auto",
                                    backgroundPosition:
                                      option.id === "original"
                                        ? "center"
                                        : "auto",
                                  }}
                                  title={option.name}
                                />
                              ))}
                            </div>
                          </div>

                          {/* Preview */}
                          <div className="aspect-[4/3] bg-gray-100 rounded-xl sm:rounded-2xl overflow-hidden border relative">
                            {getPreviewImageUrl(selectedBackground) ? (
                              <>
                                <img
                                  src={getPreviewImageUrl(selectedBackground)}
                                  alt="Space preview"
                                  className="w-full h-full object-cover"
                                />
                                {/* Loading overlay for composite generation */}
                                {isGeneratingComposite &&
                                  selectedBackground !== "original" && (
                                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                      <div
                                        className={`${useDarkGlass ? "bg-black/90" : "bg-white/90"} rounded-lg px-3 py-2 flex items-center gap-2`}
                                      >
                                        <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                        <span
                                          className={`text-xs sm:text-sm font-medium ${useDarkGlass ? "text-white" : "text-gray-900"}`}
                                        >
                                          Processing background...
                                        </span>
                                      </div>
                                    </div>
                                  )}
                              </>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-500">
                                <div className="text-center">
                                  <FontAwesomeIcon
                                    icon={faShare}
                                    className="text-2xl sm:text-4xl mb-2 opacity-50"
                                  />
                                  <p className="text-xs sm:text-sm">
                                    Preview not available
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Dimensions */}
                          <p
                            className={`text-center text-[10px] sm:text-xs mt-2 ${useDarkGlass ? "text-gray-400" : "text-gray-500"}`}
                          >
                            1600 x 1200px
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side - Share options */}
                  <div className="w-full lg:w-1/2 relative">
                    <div
                      className="absolute inset-0 rounded-lg sm:rounded-[2rem]"
                      style={{
                        background: useDarkGlass
                          ? "rgba(0, 0, 0, 0.6)"
                          : "rgba(255, 255, 255, 0.6)",
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",
                        border: useDarkGlass
                          ? "1px solid rgba(0, 0, 0, 0.8)"
                          : "1px solid rgba(255, 255, 255, 0.8)",
                      }}
                    />

                    <div className="relative flex flex-col h-full p-3 sm:p-6">
                      <AnimatePresence mode="wait">
                        {!showDribbbleForm ? (
                          // Show share options and Dribbble button when form is not open
                          <motion.div
                            key="share-options"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                            className="h-full flex flex-col"
                          >
                            {/* Share options */}
                            <div className="mb-4 sm:mb-6">
                              <h3
                                className={`text-base sm:text-lg font-semibold mb-3 sm:mb-4 ${useDarkGlass ? "text-white" : "text-gray-900"}`}
                              >
                                Share your page
                              </h3>
                              <div className="grid grid-cols-1 gap-2 sm:gap-3">
                                {shareOptions.map((option) => (
                                  <button
                                    key={option.id}
                                    onClick={option.action}
                                    className="flex items-center gap-3 p-3 rounded-xl bg-white/60 hover:bg-white/80 transition-colors border border-gray-200 text-left"
                                  >
                                    <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                      <FontAwesomeIcon
                                        icon={
                                          copiedStates[option.name]
                                            ? faCheck
                                            : option.icon
                                        }
                                        className={`text-xs sm:text-sm ${
                                          copiedStates[option.name]
                                            ? "text-green-600"
                                            : "text-gray-600"
                                        }`}
                                      />
                                    </div>
                                    <span
                                      className={`font-medium text-sm sm:text-base ${useDarkGlass ? "text-white" : "text-gray-900"}`}
                                    >
                                      {option.name}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Dribbble section */}
                            <div className="flex-1">
                              <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-pink-200">
                                <button
                                  onClick={() => {
                                    if (dribbbleAuthStatus === "connected") {
                                      setShowDribbbleForm(true);
                                    } else {
                                      handleDribbbleConnect();
                                    }
                                  }}
                                  disabled={
                                    isConnectingDribbble ||
                                    dribbbleAuthStatus === "checking"
                                  }
                                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/60 hover:bg-white/80 transition-colors border border-pink-200 text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-pink-500 flex items-center justify-center">
                                    <FontAwesomeIcon
                                      icon={faBasketball}
                                      className="text-white text-xs sm:text-sm"
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900 group-hover:text-pink-600 transition-colors text-sm sm:text-base">
                                      {dribbbleAuthStatus === "checking"
                                        ? "Checking Dribbble connection..."
                                        : dribbbleAuthStatus === "connected"
                                          ? "Create a Dribbble shot"
                                          : isConnectingDribbble
                                            ? "Connecting to Dribbble..."
                                            : "Connect to Dribbble"}
                                    </h3>
                                    <p className="text-xs sm:text-sm text-gray-600">
                                      {dribbbleAuthStatus === "checking"
                                        ? "Please wait..."
                                        : dribbbleAuthStatus === "connected"
                                          ? "Share your Qensa space with the design community"
                                          : "Connect your account to share with the design community"}
                                    </p>
                                  </div>
                                  {dribbbleAuthStatus === "checking" ||
                                  isConnectingDribbble ? (
                                    <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <FontAwesomeIcon
                                      icon={faArrowRight}
                                      className="text-pink-500 group-hover:translate-x-1 transition-transform text-sm"
                                    />
                                  )}
                                </button>

                                {/* More ways to share coming soon */}
                                <div className="mt-2 sm:mt-3 text-center">
                                  <p
                                    className={`text-[10px] sm:text-xs italic ${useDarkGlass ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    More ways to share coming soon
                                  </p>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ) : (
                          // Show full Dribbble form when open - replaces everything
                          <motion.div
                            key="dribbble-form"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                            className="h-full flex flex-col"
                          >
                            {/* Header with back button */}
                            <div className="flex items-center gap-3 mb-4 sm:mb-6">
                              <button
                                onClick={() => setShowDribbbleForm(false)}
                                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                              >
                                <FontAwesomeIcon
                                  icon={faArrowLeft}
                                  className="text-gray-600 text-xs sm:text-sm"
                                />
                              </button>
                              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-pink-500 flex items-center justify-center">
                                <FontAwesomeIcon
                                  icon={faBasketball}
                                  className="text-white text-xs sm:text-sm"
                                />
                              </div>
                              <h3
                                className={`text-base sm:text-lg font-semibold ${useDarkGlass ? "text-white" : "text-gray-900"}`}
                              >
                                Create a Dribbble shot
                              </h3>
                            </div>

                            {/* Form content - takes full space */}
                            <div className="flex-1 bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-pink-200 overflow-y-auto">
                              <div className="space-y-3 sm:space-y-4">
                                <div>
                                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    Title
                                  </label>
                                  <input
                                    type="text"
                                    value={dribbbleTitle}
                                    onChange={(e) =>
                                      setDribbbleTitle(e.target.value)
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                                    placeholder="Enter shot title"
                                  />
                                </div>

                                <div>
                                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    Description
                                  </label>
                                  <textarea
                                    value={dribbbleDescription}
                                    onChange={(e) =>
                                      setDribbbleDescription(e.target.value)
                                    }
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none text-sm"
                                    placeholder="Describe your shot"
                                  />
                                </div>

                                <div>
                                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    Tags (comma-separated)
                                  </label>
                                  <input
                                    type="text"
                                    value={dribbbleTags}
                                    onChange={(e) =>
                                      setDribbbleTags(e.target.value)
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                                    placeholder="qensa, portfolio, design"
                                  />
                                </div>

                                <div className="space-y-3 pt-2">
                                  <button
                                    onClick={handleDribbbleSubmit}
                                    disabled={isSubmittingToDribbble}
                                    className="w-full bg-pink-500 hover:bg-pink-600 disabled:bg-pink-300 text-white px-4 py-3 rounded-lg font-medium transition-colors text-sm"
                                  >
                                    {isSubmittingToDribbble ? (
                                      <div className="flex items-center justify-center gap-2">
                                        <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Submitting...
                                      </div>
                                    ) : (
                                      "Submit to Dribbble"
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </TooltipProvider>
  );

  // QR Code Modal
  const qrCodeModal = showQRCode && (
    <TooltipProvider>
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/20 backdrop-blur-sm p-4"
          onClick={() => setShowQRCode(false)}
        >
          <motion.div
            initial={{
              scale: 0.7,
              opacity: 0,
              y: 60,
              rotateX: 15,
            }}
            animate={{
              scale: 1,
              opacity: 1,
              y: 0,
              rotateX: 0,
            }}
            exit={{
              scale: 0.8,
              opacity: 0,
              y: -40,
              rotateX: -10,
            }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 300,
              mass: 0.8,
              duration: 0.4,
            }}
            className="relative w-full max-w-sm sm:max-w-md"
            onClick={(e) => e.stopPropagation()}
            style={{
              transformStyle: "preserve-3d",
              perspective: "1000px",
            }}
          >
            {/* Main glass container */}
            <div
              className="absolute inset-0 rounded-lg sm:rounded-[2rem]"
              style={{
                background: useDarkGlass
                  ? "rgba(0, 0, 0, 0.2)"
                  : "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(40px)",
                WebkitBackdropFilter: "blur(40px)",
                border: useDarkGlass
                  ? "1px solid rgba(0, 0, 0, 0.3)"
                  : "1px solid rgba(255, 255, 255, 0.3)",
                boxShadow: useDarkGlass
                  ? "0 8px 32px 0 rgba(0, 0, 0, 0.3)"
                  : "0 8px 32px 0 rgba(0, 0, 0, 0.1)",
              }}
            />

            {/* Close button */}
            <div className="absolute -top-2 -right-2 z-50">
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ delay: 0.1, duration: 0.2 }}
                    onClick={() => setShowQRCode(false)}
                    className={`w-10 h-10 sm:w-8 sm:h-8 rounded-full transition-colors flex items-center justify-center ${
                      useDarkGlass
                        ? "bg-white/10 hover:bg-white/20"
                        : "bg-black/10 hover:bg-black/20"
                    }`}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FontAwesomeIcon
                      icon={faTimes}
                      className={`${useDarkGlass ? "text-white" : "text-gray-700"} text-lg sm:text-sm`}
                    />
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-gray-900">
                  <p>Close</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* QR Code Content */}
            <div className="relative p-6 sm:p-8">
              <div
                className="absolute inset-0 rounded-lg sm:rounded-[2rem]"
                style={{
                  background: useDarkGlass
                    ? "rgba(0, 0, 0, 0.6)"
                    : "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  border: useDarkGlass
                    ? "1px solid rgba(0, 0, 0, 0.8)"
                    : "1px solid rgba(255, 255, 255, 0.8)",
                }}
              />

              <div className="relative text-center">
                {/* Header */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                  className="flex items-center justify-center space-x-3 mb-4 sm:mb-6"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      delay: 0.3,
                      type: "spring",
                      stiffness: 300,
                    }}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-primary to-accent-blue flex items-center justify-center"
                  >
                    <FontAwesomeIcon
                      icon={faQrcode}
                      className="text-white text-sm sm:text-lg"
                    />
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.35, duration: 0.3 }}
                  >
                    <h2
                      className={`text-lg sm:text-xl font-semibold ${useDarkGlass ? "text-white" : "text-gray-900"}`}
                    >
                      QR Code
                    </h2>
                    <p
                      className={`text-xs sm:text-sm ${useDarkGlass ? "text-gray-300" : "text-gray-600"}`}
                    >
                      Scan to visit your space
                    </p>
                  </motion.div>
                </motion.div>

                {/* QR Code */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                  className="flex justify-center mb-4 sm:mb-6"
                >
                  <div className="bg-white p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-lg">
                    <QensaQRCode
                      value={`${window.location.origin}/${spaceData?.space_data?.slug}`}
                      size={window.innerWidth < 640 ? 180 : 240}
                      errorCorrectionLevel="H"
                    />
                  </div>
                </motion.div>

                {/* URL */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                  className="text-center"
                >
                  <p
                    className={`text-xs sm:text-sm mb-2 ${useDarkGlass ? "text-gray-300" : "text-gray-600"}`}
                  >
                    Share this URL:
                  </p>
                  <code className="text-primary bg-primary/10 px-3 py-2 rounded-lg text-xs sm:text-sm border border-primary/20 block break-all">
                    qensa.me/{spaceData?.space_data?.slug}
                  </code>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </TooltipProvider>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      {showQRCode && createPortal(qrCodeModal, document.body)}
    </>
  );
};

export default ShareModal;
